import {
  ButtonIcon,
  FlexContainer,
  useClickOutside,
  useModal,
} from "@/lib/main";
import { useEffect, useRef, type CSSProperties } from "react";

type Props = {
  maxContentHeight?: CSSProperties["maxHeight"];
  maxContentWidth?: CSSProperties["maxWidth"];
};

export default function Modal({ maxContentHeight, maxContentWidth }: Props) {
  const { showModal, content, modalRef, closeModal } = useModal();
  const contentContainerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // * close if user clicks outside container
  const handleClickOutside = useClickOutside(contentContainerRef, () => {
    if (closeModal) closeModal();
  });

  useEffect(() => {
    if (showModal) {
      buttonRef.current?.blur();
    }
  }, [showModal]);

  useEffect(() => {
    if (!showModal) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && closeModal) {
        closeModal();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showModal, closeModal]);

  const modalStyle: CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,
    zIndex: 1000,

    display: "flex",
    justifyContent: "center",
    alignItems: "center",

    backgroundColor: "rgba(0, 0, 0, 0.8)",

    padding: "var(--page-padding-vertical) var(--page-padding-horizontal)",

    height: "100dvh",
    maxHeight: "100dvh",
    width: "100dvw",
    maxWidth: "100dvw",
  };

  const contentContainerStyle: CSSProperties = {
    backgroundColor: "var(--color-background-frame)",
    borderRadius: "var(--border-radius-lg)",

    padding: "var(--spacing-xl)",

    width: "100%",
    maxWidth: maxContentWidth ?? "60ch",
    maxHeight:
      maxContentHeight ??
      "calc(100dvh - var(--navbar-height) - var(--tabbar-height))",
    overflowY: "auto",
  };

  return (
    <>
      {content ? (
        <div
          className={showModal ? "animation__fade--in" : "animation__fade--out"}
          onClick={handleClickOutside}
          ref={modalRef}
          style={modalStyle}
        >
          <div ref={contentContainerRef} style={contentContainerStyle}>
            <FlexContainer justify="end" fullWidth>
              <ButtonIcon
                buttonRef={buttonRef}
                iconName="close"
                onClick={closeModal}
                size="md"
              />
            </FlexContainer>
            {content}
          </div>
        </div>
      ) : null}
    </>
  );
}
